import React, { useState, useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { Download, Maximize2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '';

const HeatmapMatrix = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCell, setSelectedCell] = useState(null);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const svgRef = useRef();
  const containerRef = useRef();

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (data && svgRef.current) {
      drawHeatmap();
    }
  }, [data]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/trm/products`);
      if (!response.ok) throw new Error('Failed to fetch data');
      const result = await response.json();
      processData(result.products || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const processData = (products) => {
    // Store all products for detail view
    setAllProducts(products);

    // Build matrix: categories × manufacturers
    const matrix = {};
    const categories = new Set();
    const manufacturers = new Set();

    products.forEach(p => {
      categories.add(p.trm_category);
      manufacturers.add(p.manufacturer_name);

      const key = `${p.trm_category}|||${p.manufacturer_name}`;
      if (!matrix[key]) {
        matrix[key] = {
          category: p.trm_category,
          manufacturer: p.manufacturer_name,
          count: 0,
          products: [],
          productDetails: []
        };
      }
      matrix[key].count += 1;
      matrix[key].products.push(p.canonical_name);
      matrix[key].productDetails.push({
        name: p.canonical_name,
        versions: p.version_count,
        status: p.record_status,
        id: p.product_id
      });
    });

    setData({
      matrix: Object.values(matrix),
      categories: Array.from(categories).sort(),
      manufacturers: Array.from(manufacturers).sort().slice(0, 20) // Top 20 manufacturers
    });
  };

  const drawHeatmap = () => {
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const container = containerRef.current;
    const margin = { top: 100, right: 40, bottom: 40, left: 150 };
    const width = container.clientWidth - margin.left - margin.right;
    const cellSize = 60;
    const height = data.categories.length * cellSize;

    svg.attr('width', width + margin.left + margin.right)
       .attr('height', height + margin.top + margin.bottom);

    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Scales
    const x = d3.scaleBand()
      .domain(data.manufacturers)
      .range([0, width])
      .padding(0.05);

    const y = d3.scaleBand()
      .domain(data.categories)
      .range([0, height])
      .padding(0.05);

    const maxCount = d3.max(data.matrix, d => d.count) || 1;
    const colorScale = d3.scaleSequential()
      .domain([0, maxCount])
      .interpolator(d3.interpolateBlues);

    // Tooltip
    const tooltip = d3.select('body').append('div')
      .attr('class', 'trm-heatmap-tooltip')
      .style('position', 'absolute')
      .style('visibility', 'hidden')
      .style('background', 'rgba(0, 0, 0, 0.9)')
      .style('color', 'white')
      .style('padding', '12px')
      .style('border-radius', '8px')
      .style('font-size', '13px')
      .style('pointer-events', 'none')
      .style('z-index', '10000')
      .style('box-shadow', '0 4px 12px rgba(0,0,0,0.3)');

    // Draw cells
    const cells = g.selectAll('rect')
      .data(data.matrix)
      .enter()
      .append('rect')
      .attr('x', d => x(d.manufacturer))
      .attr('y', d => y(d.category))
      .attr('width', x.bandwidth())
      .attr('height', y.bandwidth())
      .attr('fill', d => d.count > 0 ? colorScale(d.count) : '#f3f4f6')
      .attr('stroke', '#fff')
      .attr('stroke-width', 2)
      .attr('rx', 4)
      .style('cursor', 'pointer')
      .on('mouseover', function(event, d) {
        d3.select(this)
          .attr('stroke', '#3b82f6')
          .attr('stroke-width', 3);
        
        tooltip.style('visibility', 'visible')
          .html(`
            <div style="font-weight: 600; margin-bottom: 8px;">${d.manufacturer}</div>
            <div style="color: var(--cares-blue); margin-bottom: 4px;">${d.category}</div>
            <div style="font-size: 18px; font-weight: 700; color: var(--cares-blue); margin: 8px 0;">${d.count} product${d.count !== 1 ? 's' : ''}</div>
            ${d.products.length > 0 ? `<div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid var(--cares-line);">
              ${d.products.map(p => `<div style="color: var(--cares-text-dim); font-size: 11px;">• ${p}</div>`).join('')}
            </div>` : ''}
          `);
      })
      .on('mousemove', function(event) {
        tooltip
          .style('top', (event.pageY - 10) + 'px')
          .style('left', (event.pageX + 15) + 'px');
      })
      .on('mouseout', function() {
        d3.select(this)
          .attr('stroke', '#fff')
          .attr('stroke-width', 2);
        tooltip.style('visibility', 'hidden');
      })
      .on('click', function(event, d) {
        if (d.count > 0) {
          event.stopPropagation();
          setSelectedCell(d);
          setShowDetailPanel(true);
        }
      });

    // Add count labels
    g.selectAll('text.cell-label')
      .data(data.matrix.filter(d => d.count > 0))
      .enter()
      .append('text')
      .attr('class', 'cell-label')
      .attr('x', d => x(d.manufacturer) + x.bandwidth() / 2)
      .attr('y', d => y(d.category) + y.bandwidth() / 2)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .attr('fill', d => d.count > maxCount / 2 ? 'white' : '#1f2937')
      .attr('font-size', '14px')
      .attr('font-weight', '700')
      .attr('pointer-events', 'none')
      .text(d => d.count);

    // X-axis (manufacturers)
    g.append('g')
      .attr('transform', `translate(0, -10)`)
      .selectAll('text')
      .data(data.manufacturers)
      .enter()
      .append('text')
      .attr('x', d => x(d) + x.bandwidth() / 2)
      .attr('y', 0)
      .attr('text-anchor', 'start')
      .attr('transform', d => `rotate(-45, ${x(d) + x.bandwidth() / 2}, 0)`)
      .attr('font-size', '11px')
      .attr('fill', '#6b7280')
      .text(d => d.length > 15 ? d.substring(0, 15) + '...' : d);

    // Y-axis (categories)
    g.append('g')
      .selectAll('text')
      .data(data.categories)
      .enter()
      .append('text')
      .attr('x', -10)
      .attr('y', d => y(d) + y.bandwidth() / 2)
      .attr('text-anchor', 'end')
      .attr('dominant-baseline', 'middle')
      .attr('font-size', '12px')
      .attr('font-weight', '600')
      .attr('fill', '#1f2937')
      .text(d => d);

    return () => {
      tooltip.remove();
    };
  };

  const exportSVG = () => {
    const svgElement = svgRef.current;
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svgElement);
    const blob = new Blob([svgString], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'trm-heatmap.svg';
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center' }}>Loading heatmap...</div>;
  if (error) return <div style={{ padding: '40px', color: 'var(--cares-orange)' }}>Error: {error}</div>;

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--cares-text-2)', marginBottom: '8px' }}>
            TRM Heatmap Matrix
          </h1>
          <p style={{ fontSize: '14px', color: 'var(--cares-text-label)' }}>
            Category × Manufacturer product distribution
          </p>
        </div>
        <button
          onClick={exportSVG}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            background: 'var(--cares-blue)',
            color: 'var(--cares-text-max)',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600'
          }}
        >
          <Download size={16} />
          Export SVG
        </button>
      </div>

      {/* Visualization */}
      <div ref={containerRef} style={{ background: 'var(--cares-surface-card)', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflowX: 'auto' }}>
        <svg ref={svgRef}></svg>
      </div>

      {/* Instructions */}
      <div style={{ marginTop: '16px', padding: '16px', background: 'color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card))', borderRadius: '8px', fontSize: '13px', color: 'var(--cares-blue)' }}>
        <strong>💡 Tip:</strong> Hover for quick details • <strong>Click cells</strong> to see full product list and analysis
      </div>

      {/* Detail Panel */}
      {showDetailPanel && selectedCell && (
        <div style={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: '450px',
          height: '100vh',
          background: 'var(--cares-surface-card)',
          boxShadow: '-4px 0 12px rgba(0,0,0,0.15)',
          zIndex: 1000,
          overflowY: 'auto',
          padding: '24px'
        }}>
          {/* Close button */}
          <button
            onClick={() => setShowDetailPanel(false)}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: 'var(--cares-orange)',
              color: 'var(--cares-text-max)',
              border: 'none',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              cursor: 'pointer',
              fontSize: '18px',
              fontWeight: 'bold'
            }}
          >
            ×
          </button>

          {/* Header */}
          <div style={{ marginBottom: '24px', paddingRight: '40px' }}>
            <div style={{ fontSize: '12px', color: 'var(--cares-text-label)', marginBottom: '8px', textTransform: 'uppercase', fontWeight: '600' }}>
              Cell Analysis
            </div>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: 'var(--cares-text-2)', marginBottom: '8px' }}>
              {selectedCell.manufacturer}
            </h2>
            <div style={{
              display: 'inline-block',
              padding: '4px 12px',
              background: '#3b82f611',
              color: 'var(--cares-blue)',
              borderRadius: '12px',
              fontSize: '13px',
              fontWeight: '600'
            }}>
              {selectedCell.category}
            </div>
          </div>

          {/* Summary Stats */}
          <div style={{ marginBottom: '24px', padding: '16px', background: 'var(--cares-surface-board)', borderRadius: '8px' }}>
            <div style={{ fontSize: '12px', color: 'var(--cares-text-label)', marginBottom: '8px' }}>Total Products</div>
            <div style={{ fontSize: '32px', fontWeight: 'bold', color: 'var(--cares-blue)' }}>
              {selectedCell.count}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--cares-text-label)', marginTop: '8px' }}>
              Total Versions: {selectedCell.productDetails.reduce((sum, p) => sum + p.versions, 0)}
            </div>
          </div>

          {/* Product List */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--cares-text-2)', marginBottom: '16px' }}>
              Products in this cell
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {selectedCell.productDetails
                .sort((a, b) => b.versions - a.versions)
                .map((product, idx) => (
                  <div
                    key={idx}
                    style={{
                      padding: '16px',
                      background: 'var(--cares-surface-card)',
                      border: '1px solid var(--cares-line-card)',
                      borderRadius: '8px',
                      borderLeft: '4px solid var(--cares-blue)'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--cares-text-2)', flex: 1 }}>
                        {product.name}
                      </div>
                      <div style={{
                        padding: '2px 8px',
                        background: product.status === 'Active' ? '#10b98122' : '#f59e0b22',
                        color: product.status === 'Active' ? 'var(--cares-green)' : 'var(--cares-amber)',
                        borderRadius: '8px',
                        fontSize: '11px',
                        fontWeight: '600',
                        whiteSpace: 'nowrap',
                        marginLeft: '8px'
                      }}>
                        {product.status}
                      </div>
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--cares-text-label)' }}>
                      {product.versions} version{product.versions !== 1 ? 's' : ''}
                    </div>
                    {product.versions > 25 && (
                      <div style={{ marginTop: '8px', fontSize: '11px', color: 'var(--cares-amber)', background: 'color-mix(in srgb, var(--cares-amber) 22%, var(--cares-surface-card))', padding: '6px 8px', borderRadius: '4px' }}>
                        ⚠️ High version count - consider consolidation
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </div>

          {/* Analysis */}
          <div style={{ padding: '16px', background: 'color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card))', borderRadius: '8px', borderLeft: '4px solid var(--cares-blue)' }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--cares-blue)', marginBottom: '8px' }}>
              📊 Analysis
            </div>
            <div style={{ fontSize: '13px', color: 'var(--cares-blue)', lineHeight: '1.6' }}>
              {selectedCell.count === 1 && (
                <div>• Single product in this category - low redundancy</div>
              )}
              {selectedCell.count >= 2 && selectedCell.count <= 3 && (
                <div>• Moderate product count - good diversification</div>
              )}
              {selectedCell.count > 3 && (
                <div>• High product count - potential consolidation opportunity</div>
              )}
              <div style={{ marginTop: '8px' }}>
                • Average versions per product: {(selectedCell.productDetails.reduce((sum, p) => sum + p.versions, 0) / selectedCell.count).toFixed(1)}
              </div>
              {selectedCell.productDetails.some(p => p.status !== 'Active') && (
                <div style={{ marginTop: '8px', color: 'var(--cares-amber)' }}>
                  ⚠️ Contains retired/inactive products
                </div>
              )}
            </div>
          </div>

          {/* Recommendations */}
          <div style={{ marginTop: '16px', padding: '16px', background: 'color-mix(in srgb, var(--cares-green) 14%, var(--cares-surface-card))', borderRadius: '8px', borderLeft: '4px solid var(--cares-green)' }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--cares-green)', marginBottom: '8px' }}>
              💡 Recommendations
            </div>
            <div style={{ fontSize: '13px', color: 'var(--cares-green)', lineHeight: '1.6' }}>
              {selectedCell.count > 3 && (
                <div>• Review for consolidation - multiple products in same category from same vendor</div>
              )}
              {selectedCell.productDetails.filter(p => p.versions > 20).length > 0 && (
                <div style={{ marginTop: '8px' }}>• High-version products detected - implement version control policy</div>
              )}
              {selectedCell.productDetails.filter(p => p.status !== 'Active').length > 0 && (
                <div style={{ marginTop: '8px' }}>• Plan migration from retired products</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Overlay */}
      {showDetailPanel && (
        <div
          onClick={() => setShowDetailPanel(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.3)',
            zIndex: 999
          }}
        />
      )}
    </div>
  );
};

export default HeatmapMatrix;
